import type { Lang } from "./i18n";
import type { MediaItem } from "./types";
import type { Status } from "./status";
import { DOMAINS, SITE } from "./domains.ts";

export type { MediaItem };
export type ProjectCategory = "web" | "desktop" | "bot" | "tool" | "fun" | "app";
export type ProjectStatus = Status;

export interface Project {
  id: number;
  slug: string;
  name: string;
  desc: Record<Lang, string>;
  longDesc?: Record<Lang, string>;
  tags: string[];
  category: ProjectCategory;
  status: ProjectStatus;
  year: number;
  github?: string;
  gitlab?: string;
  live?: string;
  isPrivate?: boolean;
  media?: MediaItem[];
  progress?: number;
}

export const PROJECTS: Project[] = [
  {
    id: 1,
    slug: "sp4r0ww-com",
    name: DOMAINS.root,
    desc: {
      en: "personal website with live spotify, steam and git activity widgets. built with vanilla html/css/js on cloudflare pages.",
      jp: "Spotify、Steam、Gitアクティビティのライブウィジェットを備えた個人サイト。Cloudflare Pages上のバニラHTML/CSS/JSで構築。",
    },
    longDesc: {
      en: "personal website featuring: live Spotify now-playing widget with progress bar, Steam activity widget showing current/recent games, GitHub + GitLab contribution graphs for the last 28 days, a library page with books/projects/games/music filterable by category, notes and reviews system with track previews, liberty widget with glitch animation cycling 'freedom' in 70+ languages, and time-based backgrounds.",
      jp: "個人Webサイト。Spotifyリアルタイム再生ウィジェット、Steamアクティビティウィジェット、過去28日のGitHub + GitLabコントリビューショングラフ、カテゴリフィルタリング可能なライブラリページ、ノート＆レビューシステム、70以上の言語で「自由」を表示するグリッチアニメーション、時間帯別背景などを搭載。",
    },
    tags: ["javascript", "html", "css", "cloudflare"],
    category: "web",
    status: "live",
    year: 2026,
    gitlab: "https://gitlab.com/sp4r0ww/sp4r0ww.com",
    live: SITE.main,
  },
  {
    id: 2,
    slug: "booru-dl",
    name: "booru.dl",
    desc: {
      en: "booru image scraper and browser. paginated grid, lightbox, tag autocomplete, multi-job download queue, duplicate detection, filename templates.",
      jp: "booruイメージスクレイパー＆ブラウザ。ページネーショングリッド、ライトボックス、タグ補完、マルチジョブダウンロードキュー、重複検出、ファイル名テンプレート。",
    },
    longDesc: {
      en: "an Electron desktop app for browsing and downloading images from booru sites. supports Moebooru, Danbooru, Gelbooru, Shimmie2, and Szurubooru with auto-detection. features a paginated image/video grid, lightbox with keyboard navigation, video preview on hover, tag and blacklist autocomplete, multi-job download queue with pause/resume/stop, filename templates, MD5 duplicate detection, and subfolder sorting by rating.",
      jp: "booruサイトの画像閲覧・ダウンロード用Electronデスクトップアプリ。Moebooru、Danbooru、Gelbooru、Shimmie2、Szurubooruを自動検出でサポート。ページネーショングリッド、キーボードナビゲーション付きライトボックス、ホバー時のビデオプレビュー、タグ・ブラックリスト補完、一時停止/再開/停止付きマルチジョブダウンロードキュー、ファイル名テンプレート、MD5重複検出、レーティング別サブフォルダ整理を搭載。",
    },
    tags: ["electron", "python", "flask", "javascript"],
    category: "desktop",
    status: "stable",
    year: 2026,
    gitlab: "https://gitlab.com/sp4r0ww/booru.dl",
  },
  {
    id: 3,
    slug: "rand-liminal",
    name: "rand_liminal",
    desc: {
      en: "procedurally generated liminal ambient music with a glitch visualizer. no two runs are the same.",
      jp: "グリッチビジュアライザー付きプロシージャル生成リミナルアンビエント音楽。同じ実行は二度とない。",
    },
    longDesc: {
      en: "a scilab script that generates and plays ambient music in real time while displaying a minimal glitch art visualizer. chord progressions loop over C→Am→F→G with bass, pad, and melody layers synthesized from sine waves. melody has a base motif that randomly mutates each run. visuals: black void, flickering white noise, occasional glitch lines, and text that reminds you that you shouldn't be here.",
      jp: "最小限のグリッチアートビジュアライザーを表示しながらリアルタイムでアンビエント音楽を生成・再生するSciLabスクリプト。C→Am→F→Gのコード進行にベース、パッド、メロディーレイヤーをサイン波で合成。メロディーは毎回ランダムに変異するベースモチーフを持つ。",
    },
    tags: ["scilab"],
    category: "fun",
    status: "experimental",
    year: 2026,
    gitlab: "https://gitlab.com/sp4r0ww/rand_liminal",
  },
  {
    id: 4,
    slug: "maria-bot",
    name: "maria_bot",
    desc: {
      en: "private moderation bot for discord. handles server moderation with custom commands and automation.",
      jp: "Discord用プライベートモデレーションボット。カスタムコマンドと自動化でサーバーモデレーションを管理。",
    },
    tags: ["typescript", "javascript", "discord"],
    category: "bot",
    status: "live",
    year: 2025,
    isPrivate: true,
  },
  {
    id: 5,
    slug: "econ-bot-v2",
    name: "econ_bot_v2",
    desc: {
      en: "private discord economy bot with a full REST API, react dashboard, and postgresql backend.",
      jp: "フルREST API、Reactダッシュボード、PostgreSQLバックエンドを備えたプライベートDiscordエコノミーボット。",
    },
    tags: ["rust", "react", "postgresql", "discord"],
    category: "bot",
    status: "live",
    year: 2026,
    isPrivate: true,
  },
  {
    id: 6,
    slug: "lab-sp4r0ww-com",
    name: "The Lab",
    desc: {
      en: "this site. project showcase and experiment space, built with react + vite + typescript.",
      jp: "このサイト。React + Vite + TypeScriptで構築したプロジェクトショーケースと実験スペース。",
    },
    longDesc: {
      en: "a dedicated showcase for things that are too specific, unfinished, experimental, or product-shaped to live comfortably on the main personal site. it keeps projects and products separate, supports bilingual English/Japanese copy, includes persistent theme and language settings, and gives each entry a detail page with status, stack, links, media, and longer notes.",
      jp: "メインの個人サイトだけでは置ききれない、特定用途のもの、未完成のもの、実験的なもの、プロダクトに近いものをまとめるための専用ショーケース。プロジェクトとプロダクトを分けて表示し、英語/日本語の切り替え、テーマと言語の保存、各項目の詳細ページ、ステータス、技術スタック、リンク、メディア、長めの説明を扱えるようにしている。",
    },
    tags: ["react", "typescript", "vite", "cloudflare"],
    category: "web",
    status: "live",
    year: 2026,
    gitlab: "https://gitlab.com/sp4r0ww/lab.sp4r0ww.com",
    live: SITE.lab,
  },
  {
    id: 7,
    slug: "hideout-sp4r0ww-com",
    name: "Hideout",
    desc: {
      en: "Hideout is a personal microblog for thoughts, notes, updates, and other mental noise.",
      jp: "Hideoutは、考えごとやメモ、近況、頭の中のノイズをゆるく置いておく個人用マイクロブログです。",
    },
    longDesc: {
      en: "a lightweight personal microblog built for short posts, notes, updates, and loose fragments that do not need the weight of a full article. it focuses on quick publishing, readable archives, RSS output, media-friendly posts, and a quiet interface for keeping small thoughts online without turning them into a production.",
      jp: "短い投稿、メモ、近況、まとまりきらない断片を置くための軽量な個人マイクロブログ。長い記事にするほどではない内容を素早く公開できる場所として、読みやすいアーカイブ、RSS出力、メディア付き投稿、静かなUIを重視している。",
    },
    tags: ["vue", "typescript", "vite", "cloudflare"],
    category: "web",
    status: "live",
    year: 2026,
    gitlab: "https://gitlab.com/sp4r0ww/hideout.sp4r0ww.com",
    live: SITE.hideout,
  },
  {
    id: 8,
    slug: "copulum-project",
    name: "Copulum",
    desc: {
      en: "Copulum is a desktop gaming hub that unifies Steam, Epic, GOG, and Xbox libraries into one local-first profile.",
      jp: "CopulumはSteam、Epic、GOG、Xboxのライブラリを一つのローカルファーストなプロフィールに統合するデスクトップ向けゲーミングハブです。",
    },
    longDesc: {
      en: "a desktop-first gaming hub that connects PC platforms into one coherent profile. it scans installed libraries across Steam, Epic, GOG, and Xbox, normalizes them into canonical game entries, and launches everything from a single place - no caring which store owns the game. local-first by design: no cloud dependency, no social layer until the data model holds. a game is not where it was bought. a game is where it was lived.",
      jp: "PC上の複数プラットフォームを一つの統一されたプロフィールにまとめるデスクトップ向けゲーミングハブ。Steam、Epic、GOG、Xboxのインストール済みライブラリをスキャンし、正規化されたゲームエントリとして統合、どのストアが所有しているかを気にせず一箇所から起動できる。ローカルファースト設計でクラウド依存はなく、データモデルが固まるまでソーシャル機能も追加しない。ゲームはどこで買ったかではなく、どこで生きたかで決まる。",
    },
    tags: ["tauri", "rust", "react", "sqlite"],
    category: "desktop",
    status: "frozen",
    year: 2026,
    isPrivate: true,
    progress: 8,
  },
  {
    id: 9,
    slug: "libreaudio-app",
    name: "LibreAudio App",
    desc: {
      en: "a native, cross-platform client app for the libreaudio ecosystem, built to stream your self-hosted Hi-Fi library anywhere without regional restrictions.",
      jp: "地域制限なしでセルフホストされたHi-Fiライブラリをどこでもストリーミングできる、LibreAudioエコシステム向けのネイティブかつクロスプラットフォーム対応のクライアントアプリ。"
    },
    longDesc: {
      en: "the frontend and client-side companion of the libreaudio ecosystem. built to eliminate corporate streaming limitations, it connects seamlessly to your personal libreaudio-server instance. it provides a lightweight, highly responsive interface for playing uncompromised FLAC audio across devices, complete with local-first caching, playlist management, and offline support. your music experience is no longer dictated by where you are, but by what you own.",
      jp: "LibreAudioエコシステムのフロントエンドおよびクライアント側アプリケーション。企業のストリーミング制限を排除するために設計され、個人のLibreAudio Serverインスタンスとシームレスに接続する。ローカルファーストのキャッシュ、プレイリスト管理、オフライン対応を備え、妥協のないFLACオーディオをデバイス間で再生するための軽量で応答性の高いインターフェースを提供する。あなたの音楽体験は、もはや自分がどこにいるかではなく、何を所有しているかによって決まる。"
    },
    tags: ["tauri", "rust", "react", "sqlite"],
    category: "app",
    status: "wip",
    year: 2026,
    gitlab: "https://gitlab.com/nullshift/libreaudio-app",
    progress: 56,
  },
  {
    id: 10,
    slug: "libreaudio-server",
    name: "LibreAudio Server",
    desc: {
      en: "a lightweight, self-hosted audio streaming server that indexes high-fidelity music libraries and serves them securely to libreaudio clients.",
      jp: "ハイファイ音楽ライブラリをインデックス化し、libreaudioクライアントに安全に配信する、軽量でセルフホスト型のオーディオストリーミングサーバー。"
    },
    longDesc: {
      en: "the backbone of the libreaudio ecosystem. a self-hosted, headless media server designed to render regional geo-blocks completely irrelevant. it scans local directories, indexes multi-format audio files with blazing speed, and serves uncompressed Hi-Fi streams securely to authorized client apps. built for ultimate privacy and control, it cuts out corporate middlemen entirely, transforming your private hardware into a personal, borderless broadcasting station.",
      jp: "LibreAudioエコシステムのバックボーン。地域のジオブロック（地域制限）を完全に無意味化するために設計された、セルフホスト型のヘッドレスメディアサーバー。ローカルディレクトリをスキャンし、マルチフォーマットのオーディオファイルを驚異的な速度でインデックス化、認証されたクライアントアプリに非圧縮のHi-Fiストリームを安全に配信する。究極のプライバシーと統制のために構築され、企業の仲介者を完全に排除することで、個人のハードウェアを国境のない独自の放送局へと変貌させる。"
    },
    tags: ["rust", "sqlite"],
    category: "tool",
    status: "stable",
    year: 2026,
    gitlab: "https://gitlab.com/nullshift/libreaudio-server",
    progress: 100,
  },
];

export const FILTERS: Array<"all" | ProjectCategory> = ["all", "web", "desktop", "bot", "tool", "fun", "app"];
